import { useQuery } from '@tanstack/react-query';
import { getDocumentViewUrl } from '../endpoints/accountDocument.ts';
import type { AccountDocumentFile } from '../model.ts';
import {
  getResponseStatus,
  getViewUrlErrorMessage,
} from '../utils/apiError.ts';
import { ACCOUNT_DOCUMENT_QUERY_NAMESPACE } from './useAccountDocumentList.ts';

const isExpired = (expireDateTime: string | undefined) =>
  !expireDateTime || new Date(expireDateTime).getTime() <= Date.now();

// 403（檔案狀態不允許取用）和 404（查無此檔）重試不會改變結果
const NON_RETRYABLE_STATUS = [403, 404];
const MAX_RETRY_COUNT = 2;

const useFileViewUrl = (file: AccountDocumentFile | undefined) => {
  const isViewable = file?.scanState === 'CLEAN';
  const needsRefresh = Boolean(
    isViewable && (!file?.viewUrl || isExpired(file?.viewUrlExpireDateTime)),
  );

  const {
    data: refreshed,
    isLoading,
    isRefetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: [ACCOUNT_DOCUMENT_QUERY_NAMESPACE, 'file-view-url', file?.id],
    queryFn: () => getDocumentViewUrl(file!.id),
    enabled: needsRefresh,
    retry: (failureCount, queryError) => {
      const status = getResponseStatus(queryError);
      if (status && NON_RETRYABLE_STATUS.includes(status)) return false;
      return failureCount < MAX_RETRY_COUNT;
    },
  });

  return {
    viewUrl: refreshed?.viewUrl ?? (needsRefresh ? undefined : file?.viewUrl),
    isLoading: needsRefresh && (isLoading || isRefetching),
    errorMessage:
      needsRefresh && isError ? getViewUrlErrorMessage(error) : undefined,
    retry: () => {
      void refetch();
    },
  };
};

export { useFileViewUrl };
